import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import api from "../../utils/api";
import {
  toLocalizedNumber,
  toLocalizedPrice,
  toLocalizedStatus,
} from "../../utils/localeUtils";
import { usePopup } from "../../context/PopupContext";
import {
  FaDollarSign,
  FaCreditCard,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaSearch,
  FaFileInvoice,
  FaUpload,
  FaBan,
  FaTimes,
  FaWallet,
  FaArrowUp,
  FaExchangeAlt,
  FaHistory,
} from "react-icons/fa";
import Pagination from "../../components/Pagination";
import "./FarmerPayments.css";

const PER_PAGE = 10;

const FarmerPayments = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = usePopup();

  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [balance, setBalance] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);

  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);
  const [withdrawError, setWithdrawError] = useState("");

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMethod, setFilterMethod] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);

  const [activeTab, setActiveTab] = useState("transactions");
  const [txPage, setTxPage] = useState(1);
  const [wdPage, setWdPage] = useState(1);

  useEffect(() => {
    const ud = localStorage.getItem("user");
    if (ud) {
      try {
        const u = JSON.parse(ud);
        if (u.role !== "farmer") {
          navigate("/");
          return;
        }
        fetchPayments();
        fetchBalance();
        fetchWithdrawals();
      } catch {
        setLoading(false);
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const res = await api.get("/payments");
      setPayments(
        (res.data || []).map((p) => ({
          ...p,
          orderId: p.order?._id || p.order || null,
          orderData: p.order?._id ? p.order : null,
        })),
      );
    } catch {
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBalance = async () => {
    try {
      const r = await api.get("/payments/balance");
      setBalance(r.data || null);
    } catch {
      setBalance(null);
    }
  };

  const fetchWithdrawals = async () => {
    try {
      const r = await api.get("/payments/withdrawals");
      setWithdrawals(Array.isArray(r.data) ? r.data : []);
    } catch {
      setWithdrawals([]);
    }
  };

  /* ---- Withdraw Modal ---- */
  const openWithdrawModal = () => {
    setWithdrawAmount("");
    setWithdrawError("");
    setShowWithdrawModal(true);
  };
  const closeWithdrawModal = () => {
    setShowWithdrawModal(false);
    setWithdrawAmount("");
    setWithdrawError("");
  };

  const handleWithdrawSubmit = async (e) => {
    e.preventDefault();
    const num = Number(withdrawAmount);
    if (!Number.isFinite(num) || num < 1) {
      setWithdrawError(t("farmerPayments.enterValidAmount"));
      return;
    }
    const avail = balance?.availableBalance ?? 0;
    if (num > avail) {
      setWithdrawError(
        t("farmerPayments.insufficientBalance", {
          amount: toLocalizedPrice(avail),
        }),
      );
      return;
    }
    setWithdrawSubmitting(true);
    setWithdrawError("");
    try {
      await api.post("/payments/withdraw", { amount: num });
      showToast(t("farmerPayments.withdrawalSubmitted"), "success");
      closeWithdrawModal();
      fetchBalance();
      fetchWithdrawals();
    } catch (err) {
      const msg =
        err.response?.data?.message || t("farmerPayments.submitFailed");
      setWithdrawError(msg);
      showToast(msg, "error");
    } finally {
      setWithdrawSubmitting(false);
    }
  };

  const handleCancelWithdrawal = async (id) => {
    setCancellingId(id);
    try {
      await api.patch(`/payments/withdrawals/${id}/cancel`);
      showToast(t("farmerPayments.withdrawalCancelled"), "success");
      fetchBalance();
      fetchWithdrawals();
    } catch (err) {
      showToast(
        err.response?.data?.message || t("farmerPayments.cancelFailed"),
        "error",
      );
    } finally {
      setCancellingId(null);
    }
  };

  const setQuick = (pct) => {
    const a = balance?.availableBalance ?? 0;
    setWithdrawAmount(
      pct === 100
        ? String(Math.floor(a))
        : pct === 50
          ? String(Math.floor(a * 0.5))
          : "",
    );
  };

  /* ---- Helpers ---- */
  const getStatusColor = (s) => {
    switch (s?.toLowerCase()) {
      case "completed":
      case "paid":
        return "#22c55e";
      case "pending":
        return "#f59e0b";
      case "failed":
        return "#ef4444";
      case "refunded":
        return "#9E9E9E";
      default:
        return "#888";
    }
  };

  const getStatusIcon = (s) => {
    switch (s?.toLowerCase()) {
      case "completed":
      case "paid":
        return <FaCheckCircle />;
      case "pending":
        return <FaClock />;
      case "failed":
      case "refunded":
        return <FaTimesCircle />;
      default:
        return <FaClock />;
    }
  };

  const fmtDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const gwName = (g) =>
    ({ khalti: "Khalti", esewa: "eSewa", ime: "IME Pay" })[g?.toLowerCase()] ||
    g ||
    "—";

  /* ---- Filtering & Pagination ---- */
  const filtered = useMemo(
    () =>
      payments.filter((p) => {
        if (
          filterStatus !== "all" &&
          p.paymentStatus?.toLowerCase() !== filterStatus
        )
          return false;
        if (
          filterMethod !== "all" &&
          p.paymentMethod?.toLowerCase() !== filterMethod.toLowerCase()
        )
          return false;
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          const match =
            p.transactionId?.toLowerCase().includes(q) ||
            p.order?._id?.toLowerCase().includes(q) ||
            p.consumer?.name?.toLowerCase().includes(q);
          if (!match) return false;
        }
        return true;
      }),
    [payments, filterStatus, filterMethod, searchQuery],
  );

  const txTotalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const txPageItems = filtered.slice(
    (txPage - 1) * PER_PAGE,
    txPage * PER_PAGE,
  );

  const wdTotalPages = Math.max(1, Math.ceil(withdrawals.length / PER_PAGE));
  const wdPageItems = withdrawals.slice(
    (wdPage - 1) * PER_PAGE,
    wdPage * PER_PAGE,
  );

  useEffect(() => {
    setTxPage(1);
  }, [filterStatus, filterMethod, searchQuery]);

  const stats = useMemo(() => {
    const total = payments.reduce((s, p) => s + (p.amount || 0), 0);
    const completed = payments
      .filter((p) =>
        ["completed", "paid"].includes(p.paymentStatus?.toLowerCase()),
      )
      .reduce((s, p) => s + (p.amount || 0), 0);
    const pending = payments
      .filter((p) => p.paymentStatus?.toLowerCase() === "pending")
      .reduce((s, p) => s + (p.amount || 0), 0);
    const online = payments
      .filter(
        (p) =>
          p.paymentMethod?.toLowerCase() === "online" &&
          ["completed", "paid"].includes(p.paymentStatus?.toLowerCase()),
      )
      .reduce((s, p) => s + (p.amount || 0), 0);
    return { total, completed, pending, online };
  }, [payments]);

  if (loading) {
    return (
      <div className="fp-page">
        <Header />
        <div className="fp-loading">
          <div className="fp-dots">
            <span />
            <span />
            <span />
          </div>
          <p>{t("farmerPayments.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fp-page">
      <Header />
      <div className="fp-container">
        {/* ---- Header ---- */}
        <div className="fp-head">
          <div>
            <h1 className="fp-title">{t("farmerPayments.title")}</h1>
            <p className="fp-subtitle">{t("farmerPayments.subtitle")}</p>
          </div>
        </div>

        {/* ---- Balance + Withdraw Card ---- */}
        <div className="fp-balance-card">
          <div className="fp-balance-left">
            <div className="fp-balance-icon">
              <FaWallet />
            </div>
            <div>
              <span className="fp-balance-label">
                {t("farmerPayments.availableToWithdraw")}
              </span>
              <p className="fp-balance-amount">
                Rs.{" "}
                {balance != null
                  ? Number(balance.availableBalance || 0).toLocaleString()
                  : "—"}
              </p>
              {balance?.pendingWithdrawalsCount > 0 && (
                <span className="fp-balance-pending">
                  {balance.pendingWithdrawalsCount} pending request
                  {balance.pendingWithdrawalsCount !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>
          <button
            className="fp-withdraw-btn"
            disabled={!(balance?.availableBalance > 0)}
            onClick={openWithdrawModal}
          >
            <FaArrowUp /> {t("farmerPayments.withdraw")}
          </button>
        </div>

        {/* ---- Stats ---- */}
        <div className="fp-stats">
          {[
            {
              icon: <FaDollarSign />,
              value: `Rs. ${stats.total.toLocaleString()}`,
              label: t("farmerPayments.totalPayments"),
              cls: "total",
            },
            {
              icon: <FaCheckCircle />,
              value: `Rs. ${stats.completed.toLocaleString()}`,
              label: t("farmerPayments.completed"),
              cls: "completed",
            },
            {
              icon: <FaClock />,
              value: `Rs. ${stats.pending.toLocaleString()}`,
              label: t("farmerPayments.pending"),
              cls: "pending",
            },
            {
              icon: <FaCreditCard />,
              value: `Rs. ${toLocalizedPrice(stats.online)}`,
              label: t("farmerPayments.onlinePayments"),
              cls: "online",
            },
          ].map((s) => (
            <div key={s.cls} className="fp-stat">
              <div className={`fp-stat-ico fp-stat-ico--${s.cls}`}>
                {s.icon}
              </div>
              <div>
                <span className="fp-stat-val">{s.value}</span>
                <span className="fp-stat-lbl">{s.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* ---- Tabs ---- */}
        <div className="fp-tabs">
          <button
            className={`fp-tab ${activeTab === "transactions" ? "fp-tab--on" : ""}`}
            onClick={() => setActiveTab("transactions")}
          >
            <FaExchangeAlt /> Transactions ({toLocalizedNumber(filtered.length)}
            )
          </button>
          <button
            className={`fp-tab ${activeTab === "withdrawals" ? "fp-tab--on" : ""}`}
            onClick={() => setActiveTab("withdrawals")}
          >
            <FaHistory /> Withdrawals ({toLocalizedNumber(withdrawals.length)})
          </button>
        </div>

        {/* ======== TRANSACTIONS TAB ======== */}
        {activeTab === "transactions" && (
          <>
            {/* Filters */}
            <div className="fp-toolbar">
              <div className="fp-search">
                <FaSearch className="fp-search-ico" />
                <input
                  type="text"
                  placeholder={t("farmerPayments.searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="fp-search-clear"
                    onClick={() => setSearchQuery("")}
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
              <div className="fp-filter-row">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="fp-select"
                >
                  <option value="all">{t("farmerPayments.allStatus")}</option>
                  <option value="completed">
                    {t("farmerPayments.completed")}
                  </option>
                  <option value="pending">{t("farmerPayments.pending")}</option>
                  <option value="failed">{t("farmerPayments.failed")}</option>
                  <option value="refunded">
                    {t("farmerPayments.refunded")}
                  </option>
                </select>
                <select
                  value={filterMethod}
                  onChange={(e) => setFilterMethod(e.target.value)}
                  className="fp-select"
                >
                  <option value="all">{t("farmerPayments.allMethods")}</option>
                  <option value="online">{t("farmerPayments.online")}</option>
                  <option value="COD">{t("farmerPayments.cod")}</option>
                </select>
              </div>
            </div>

            {/* Table */}
            <div className="fp-table-wrap">
              <table className="fp-table">
                <thead>
                  <tr>
                    <th>{t("farmerPayments.transactionId")}</th>
                    <th>{t("farmerPayments.orderId")}</th>
                    <th>{t("farmerPayments.customer")}</th>
                    <th>{t("farmerPayments.amount")}</th>
                    <th>{t("farmerPayments.method")}</th>
                    <th>{t("farmerPayments.gateway")}</th>
                    <th>{t("farmerPayments.status")}</th>
                    <th>{t("farmerPayments.date")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {txPageItems.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="fp-empty-cell">
                        <FaFileInvoice className="fp-empty-ico" />
                        <p>{t("farmerPayments.noPayments")}</p>
                      </td>
                    </tr>
                  ) : (
                    txPageItems.map((p) => (
                      <tr key={p._id}>
                        <td>
                          <span className="fp-txn">
                            {p.transactionId || "—"}
                          </span>
                        </td>
                        <td>
                          <Link
                            to={`/farmer/order/${p.orderId || p.order?._id || p.order || ""}`}
                            className="fp-order-link"
                          >
                            #
                            {(p.orderId || p.order?._id || p.order || "")
                              .toString()
                              .slice(-8) || "—"}
                          </Link>
                        </td>
                        <td>{p.consumer?.name || "—"}</td>
                        <td className="fp-amount">
                          Rs. {toLocalizedPrice(p.amount ?? 0)}
                        </td>
                        <td>
                          <span
                            className={`fp-method ${p.paymentMethod?.toLowerCase()}`}
                          >
                            {p.paymentMethod === "COD" ? "COD" : "Online"}
                          </span>
                        </td>
                        <td>
                          {p.paymentMethod?.toLowerCase() === "online"
                            ? gwName(p.paymentGateway)
                            : "—"}
                        </td>
                        <td>
                          <span
                            className="fp-badge"
                            style={{
                              background: getStatusColor(p.paymentStatus),
                            }}
                          >
                            {getStatusIcon(p.paymentStatus)}{" "}
                            {toLocalizedStatus(p.paymentStatus, t) || "—"}
                          </span>
                        </td>
                        <td className="fp-date">
                          {fmtDate(p.paidAt || p.createdAt)}
                        </td>
                        <td>
                          <button
                            className="fp-view-btn"
                            onClick={() => {
                              setSelectedPayment(p);
                              setShowModal(true);
                            }}
                          >
                            {t("farmerPayments.viewDetails")}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={txPage}
              totalPages={txTotalPages}
              onPageChange={setTxPage}
            />
          </>
        )}

        {/* ======== WITHDRAWALS TAB ======== */}
        {activeTab === "withdrawals" && (
          <>
            <div className="fp-wd-head">
              <p className="fp-wd-note">
                Funds go to the Khalti or Bank account set in your Profile. You
                can cancel pending requests anytime.
              </p>
              <button
                className="fp-withdraw-btn fp-withdraw-btn--sm"
                disabled={!(balance?.availableBalance > 0)}
                onClick={openWithdrawModal}
              >
                <FaArrowUp /> Request Withdrawal
              </button>
            </div>

            {withdrawals.length === 0 ? (
              <div className="fp-wd-empty">
                <FaUpload className="fp-wd-empty-ico" />
                <h3>No withdrawal requests yet</h3>
                <p>Request your first withdrawal using the button above.</p>
              </div>
            ) : (
              <>
                <div className="fp-wd-list">
                  {wdPageItems.map((w) => {
                    const sc =
                      {
                        pending: "#f59e0b",
                        processing: "#f59e0b",
                        completed: "#22c55e",
                        failed: "#ef4444",
                        cancelled: "#9e9e9e",
                      }[w.status] || "#888";
                    const bg =
                      {
                        pending: "#fffbeb",
                        processing: "#fffbeb",
                        completed: "#f0fdf4",
                        failed: "#fef2f2",
                        cancelled: "#f5f5f5",
                      }[w.status] || "#f5f5f5";
                    return (
                      <div
                        key={w._id}
                        className="fp-wd-card"
                        style={{ borderLeftColor: sc }}
                      >
                        <div className="fp-wd-main">
                          <span className="fp-wd-amount">
                            Rs. {Number(w.amount).toLocaleString()}
                          </span>
                          <span className="fp-wd-method">
                            {w.method === "khalti"
                              ? "Khalti"
                              : w.method === "bank"
                                ? "Bank"
                                : "Other"}
                          </span>
                          <span
                            className="fp-wd-status"
                            style={{ color: sc, background: bg }}
                          >
                            {w.status}
                          </span>
                          <span className="fp-wd-date">
                            {fmtDate(w.createdAt)}
                          </span>
                        </div>
                        {w.status === "pending" && (
                          <button
                            className="fp-wd-cancel"
                            disabled={!!cancellingId}
                            onClick={() => handleCancelWithdrawal(w._id)}
                          >
                            {cancellingId === w._id ? (
                              <span className="fp-wd-spinner" />
                            ) : (
                              <>
                                <FaBan /> Cancel
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <Pagination
                  page={wdPage}
                  totalPages={wdTotalPages}
                  onPageChange={setWdPage}
                />
              </>
            )}
          </>
        )}

        {/* ======== Detail Modal ======== */}
        {showModal && selectedPayment && (
          <div className="fp-overlay" onClick={() => setShowModal(false)}>
            <div className="fp-modal" onClick={(e) => e.stopPropagation()}>
              <div className="fp-modal-head">
                <h2>{t("farmerPayments.paymentDetails")}</h2>
                <button
                  className="fp-modal-x"
                  onClick={() => setShowModal(false)}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="fp-modal-body">
                <div className="fp-detail-grid">
                  {[
                    ["Transaction ID", selectedPayment.transactionId || "—"],
                    ["Payment ID", `#${selectedPayment._id?.slice(-8)}`],
                    [
                      "Amount",
                      `Rs. ${toLocalizedPrice(selectedPayment.amount ?? 0)}`,
                    ],
                    ["Method", selectedPayment.paymentMethod || "—"],
                    [
                      "Gateway",
                      selectedPayment.paymentMethod?.toLowerCase() === "online"
                        ? gwName(selectedPayment.paymentGateway)
                        : "—",
                    ],
                    [
                      "Status",
                      toLocalizedStatus(selectedPayment.paymentStatus, t) ||
                        "—",
                    ],
                    [
                      "Payment Date",
                      fmtDate(
                        selectedPayment.paidAt || selectedPayment.createdAt,
                      ),
                    ],
                    ["Created", fmtDate(selectedPayment.createdAt)],
                    ["Customer", selectedPayment.consumer?.name || "—"],
                    ["Email", selectedPayment.consumer?.email || "—"],
                  ].map(([label, val]) => (
                    <div key={label} className="fp-detail-cell">
                      <span className="fp-detail-label">{label}</span>
                      <span className="fp-detail-val">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="fp-modal-foot">
                <button
                  className="fp-btn-cancel"
                  onClick={() => setShowModal(false)}
                >
                  {t("farmerPayments.close")}
                </button>
                {(selectedPayment.orderId || selectedPayment.order) && (
                  <Link
                    to={`/farmer/order/${selectedPayment.orderId || selectedPayment.order?._id || selectedPayment.order || ""}`}
                    className="fp-btn-go"
                    onClick={() => setShowModal(false)}
                  >
                    {t("farmerPayments.viewOrder")}
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ======== Withdraw Modal ======== */}
        {showWithdrawModal && (
          <div className="fp-overlay" onClick={closeWithdrawModal}>
            <div
              className="fp-modal fp-modal--sm"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="fp-modal-head">
                <h2>{t("farmerPayments.requestWithdrawal")}</h2>
                <button className="fp-modal-x" onClick={closeWithdrawModal}>
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleWithdrawSubmit} className="fp-wd-form">
                <div className="fp-wd-form-avail">
                  {t("farmerPayments.available")}:{" "}
                  <strong>
                    Rs.{" "}
                    {balance != null
                      ? toLocalizedPrice(Number(balance.availableBalance || 0))
                      : "0"}
                  </strong>
                </div>
                <label className="fp-wd-form-label">
                  {t("farmerPayments.amountRs")}
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={withdrawAmount}
                  onChange={(e) => {
                    setWithdrawAmount(e.target.value);
                    setWithdrawError("");
                  }}
                  placeholder={t("farmerPayments.enterAmount")}
                  className="fp-wd-form-input"
                  disabled={withdrawSubmitting}
                />
                <div className="fp-wd-form-quick">
                  <span>{t("farmerPayments.quick")}:</span>
                  <button type="button" onClick={() => setQuick(50)}>
                    50%
                  </button>
                  <button type="button" onClick={() => setQuick(100)}>
                    100%
                  </button>
                </div>
                {withdrawError && (
                  <p className="fp-wd-form-err">{withdrawError}</p>
                )}
                <p className="fp-wd-form-note">
                  Funds go to the <strong>Khalti or Bank account</strong> in
                  your Profile.
                </p>
                <div className="fp-modal-foot">
                  <button
                    type="button"
                    className="fp-btn-cancel"
                    onClick={closeWithdrawModal}
                    disabled={withdrawSubmitting}
                  >
                    {t("farmerPayments.close")}
                  </button>
                  <button
                    type="submit"
                    className="fp-btn-go"
                    disabled={withdrawSubmitting}
                  >
                    {withdrawSubmitting
                      ? t("farmerPayments.submitting")
                      : t("farmerPayments.requestWithdrawal")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FarmerPayments;
